/**
 * 点一个字卡打开的浮窗：**「这个字的读音与造词」+ 所有操作**（2026-10-18 升级）。
 *
 * ## 用户要的那件事（原话）
 *
 * > "如果它是多音字，你最好用颜色（也就是框框的底色）去表示它，查出来之后，
 * >  **我点某一个框框，就要弹出一个浮动窗来。具体所有的操作都在这个框框里面进行**：
 * >  1. **造词**：比如针对多音字各个音的处理，**针对每个音去造词**。
 * >  2. **隐藏不常用的音**：如果某个音是不常用的，就**把它隐藏起来**。但是想看的话，
 * >     也能点开来看。"
 *
 * 所以这一屏现在装的是**一个字的全部动作**：
 *
 * | 区域 | 能做什么 |
 * | --- | --- |
 * | 收录在 | 这个字在哪本书的哪一册哪张表上（读） |
 * | 这个字还出现在 | **同一筛选结果里的其它位置**（`第 12 个 · 识字表`），**点一下跳过去**（滚 + 高亮 2 秒；**不自动开浮窗**）；只出现一次时整块不渲染 |
 * | 这个字的操作 | 标为多音字 / 标为单音字、改拼音、编辑（年级/学期/备注）、删除 |
 * | 读音分组 | 每个音各造过哪些词；每个音下面「**给这个音造词**」；每个音「**隐藏这个音**」 |
 * | 已隐藏的读音（N） | 默认收起，点开展示；每个音都能「**显示**」回来 |
 *
 * ## 三态（本项目反复踩过的那条线，这里一条都不放宽）
 *
 *   · 读音列表**拿不到**（`null`）→ 明说"没拿到"，不是"这个字没有读音"；
 *   · 某个音下**确实没有词**（`[]`）→ 说"这个音还没造过词"（那就是待办）；
 *   · 字表里没有这个字（`readings` 为空）→ 说"内置字表里没有它，读音要人工填"。
 *
 * ## 写入口**只在管理员那边渲染**
 *
 * 家长端（`canEdit === false` / 一个回调都不传）看到的是同一份**只读**内容：
 * 收录、读音、每个音的词、隐藏/显示（那只影响他自己的显示）。
 * ⛔ 不渲染"点了会 403"的灰按钮 —— 本项目明确反对那种入口。
 *
 * ## 「隐藏不常用的音」——**服务端是唯一来源**（2026-10-18 用户定稿）
 *
 * 本组件只收 `hiddenReadings` 与 `onToggleHiddenReading`（受控）：
 *   · 隐藏集合来自**接口**（`GET .../readings` 的 `hiddenReadings` / 列表 DTO 里那一份），
 *     不再有任何 `localStorage`（旧的键由页面一次性清掉，见 `lib/hanziReadingPrefs.ts`）；
 *   · **家长端只收 `hiddenReadings`、不收回调**：他看得到管理员藏了哪些
 *     （收进下面「已隐藏的读音（N）」），但没有任何"隐藏 / 显示"按钮；
 *   · 隐藏只影响这里的显示：已有的词与它们的读音（`word_hanzi.reading`）一个字都不动。
 *
 * ⚠️ `hiddenReadings` 缺失（老后端）时按空数组处理 = **全部显示**：
 *    那最多是多显示两个音，绝不会把一个还在用的音藏掉。
 */
import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Badge, Skeleton } from '../ui/Card';
import { Button } from '../ui/Button';
import { IconChevronDown } from '../ui/Icons';
import { cn } from '../../lib/utils';
import { splitReadingsByHidden } from '@fht/shared';
import type { HanziReadingsView, HanziView, TextbookView } from '../../lib/api/dictation';
import { describeHanziListings } from './hanziListings';
import { HanziJumpLinks } from './HanziJumpLinks';
import type { HanziJumpCard } from './hanziJump';
import { PinyinEditor } from './PinyinEditor';

export interface HanziReadingsModalProps {
  /** 打开的那个字（`null` = 关着）。 */
  hanzi: HanziView | null;
  /** 查到的读音汇总；`null` = **没查到**（接口失败 / 形状不符）。 */
  data: HanziReadingsView | null;
  loading: boolean;
  error: string | null;
  /**
   * 课本列表 —— 只用来把收录里的 `textbookId` 变成「人民教育出版社 · 小学一年级 · 上册」。
   * `null` = 没拿到（那一行会明说，而不是留白）。
   */
  textbooks: readonly TextbookView[] | null;
  onRetry: () => void;
  onClose: () => void;

  // ---------------------------------------------------------------- 写入口（只有管理员传）
  /** 能不能维护（家长只读）。`false` / 不传 ⇒ 下面所有按钮都不渲染。 */
  canEdit?: boolean;
  /** 正在保存「多音字标注」。 */
  polyphonicSaving?: boolean;
  /** 标为多音字 / 标为单音字（**人工确认**，不自动判断）。 */
  onTogglePolyphonic?: (() => void) | undefined;
  /** 正在显示「改拼音」的编辑框。 */
  pinyinOpen?: boolean;
  /** 点「改拼音」：展开 `PinyinEditor`。 */
  onStartPinyin?: (() => void) | undefined;
  pinyinSaving?: boolean;
  pinyinError?: string | null;
  onSavePinyin?: ((value: string) => void) | undefined;
  onCancelPinyin?: (() => void) | undefined;
  /**
   * 这次「改拼音」**会不会连带改掉这张卡（这个位置）的读音**。
   *
   * `true` = 浮窗是从「浏览与编辑」的某张字卡点开的，且那张卡有收录（`listingId`）：
   * 保存时服务端会把那个位置的显式 `reading` 一起改（否则字卡优先显示位置读音，
   * 用户改完还是看到旧的 —— 2026-10-21 用户报的 bug）。
   *
   * 界面上要**明说**这一句：用户的心智模型是"改这个字的读音"，而字卡还可能
   * 显示别的位置上单独标过的音（那些**不会**跟着改）。不说清就会再撞一次同样的困惑。
   *
   * 默认 `false`（家长端 / 没有位置的入口）⇒ 一律不渲染这句说明。
   */
  pinyinAffectsPosition?: boolean;
  /** 编辑这个字（年级 / 学期 / 备注 / 收录）—— 复用既有的「编辑生字」表单。 */
  onEditHanzi?: (() => void) | undefined;
  /** 删除这个字（走既有的确认弹窗）。 */
  onDeleteHanzi?: (() => void) | undefined;
  /**
   * 某个读音下面「给这个音造词」：传**那个读音**。
   *
   * ⚠️ 造词复用既有的「新增词语」表单；保存后页面会把"这个字在这个词里读这个音"
   *    写进 `word_hanzi.reading`（多音字按音造词）。
   */
  onCreateWord?: ((reading: string) => void) | undefined;
  /**
   * 某个读音下面「**批量造词**」：传**那个读音**。
   *
   * 2026-10-30 用户要求："每次只能造一个词，可不可以一次造多个词？"
   * 与 `onCreateWord` 并存（单个的那个留给"只想加一个、还想顺手改课本/课时"的场景）。
   */
  onBatchCreateWords?: ((reading: string) => void) | undefined;
  /**
   * 某个读音下面「**选词造词**」：传**那个读音**。
   *
   * 2026-11-04 用户要求："再加个按钮，叫「选词造词」，就是你去查权威词库，
   * 列出常用词语和成语，然后我点击选择我要的词语和成语，然后保存。"
   */
  onPickWords?: ((reading: string) => void) | undefined;
  /**
   * **改自造词的字面**（2026-10-30 用户要求："造的词好像也不能修改？万一有错别字怎么办？"）。
   *
   * 只有**自己造的词**（`fromTextbook === false`）给这个入口；课本词表是导入的事实，
   * 服务端也会拒（`updateWord` 里那条 400）。
   */
  onRenameWord?: ((wordId: string, wordText: string) => void | Promise<void>) | undefined;
  /**
   * **删除自造词**（2026-11-04 用户要求："造的词要能删除"）。
   *
   * 只对**自己造的词**给这个入口；课本词表是导入的事实，不从这里删
   * （要删课本词，走「按书录入」重贴那张表）。
   */
  onDeleteWord?: ((wordId: string, wordText: string) => void | Promise<void>) | undefined;

  // ---------------------------------------------------------------- 隐藏不常用的音（受控）
  /**
   * 这个字被隐藏的读音（**来自服务端** `hanzi.hidden_readings`；空 = 全部显示）。
   *
   * ⚠️ 家长端也传（他要**看得到**管理员藏了哪些），但那时不传
   *    `onToggleHiddenReading` —— 于是「隐藏这个音 / 显示」两个按钮都不渲染。
   */
  hiddenReadings?: readonly string[];
  /**
   * 隐藏 / 显示一个读音。
   *
   * `undefined` = 这个入口不出现：
   *   · **家长端**永远不传（改隐藏集合是 `PUT /admin/dictation/hanzi/:id/hidden-readings`，
   *     只有系统管理员能过门禁；本项目不做"点了会 403"的灰按钮）；
   *   · 维护模式下由页面传（乐观更新 + 失败回滚，见 `DictationWorkspace`）。
   */
  onToggleHiddenReading?: ((reading: string) => void) | undefined;

  // ---------------------------------------------------------------- 同一个字的其它位置（跳转）
  /**
   * **同一个字在当前这次筛选结果里的其它位置**（不含打开的这张卡）。
   *
   * 用户原话：
   * > "在「浏览」模块下面也一样：……**同一个界面中出现多音字，也能互相跳转**，
   * >  因为多音字不是相邻的，可能位于一页不同的位置。"
   *
   * ⚠️ 只在**同一屏**内跳（当前筛选结果，不跨册 / 不跨筛选）—— 由页面按当前列表算好传进来
   *    （`browseJumpCards()`）。空数组 = 这个字只出现一次 ⇒ **整块不渲染**（不留空壳）。
   */
  occurrences?: readonly HanziJumpCard[];
  /** 点「还出现在」里的某一项：跳到那张卡（滚 + 高亮 2 秒；**不打开**它的浮窗）。 */
  onJumpToOccurrence?: ((key: string) => void) | undefined;
}

export function HanziReadingsModal({
  hanzi,
  data,
  loading,
  error,
  textbooks,
  onRetry,
  onClose,
  canEdit = false,
  polyphonicSaving = false,
  onTogglePolyphonic,
  pinyinOpen = false,
  onStartPinyin,
  pinyinSaving = false,
  pinyinError = null,
  onSavePinyin,
  onCancelPinyin,
  pinyinAffectsPosition = false,
  onEditHanzi,
  onDeleteHanzi,
  onCreateWord,
  onBatchCreateWords,
  onPickWords,
  onRenameWord,
  onDeleteWord,
  hiddenReadings = [],
  onToggleHiddenReading,
  occurrences = [],
  onJumpToOccurrence,
}: HanziReadingsModalProps) {
  /**
   * ⚠️ 用 `useState` 记住"上次打开的那个字"，是为了**关闭动画期间**不要把内容
   *    立刻清空（`hanzi` 一变成 `null`，下面的字就没了，看起来像闪一下）。
   */
  const [lastHanzi, setLastHanzi] = useState<HanziView | null>(hanzi);
  useEffect(() => {
    if (hanzi !== null) setLastHanzi(hanzi);
  }, [hanzi]);
  const shown = hanzi ?? lastHanzi;

  /** 「已隐藏的读音（N）」是否展开（默认收起 —— 用户要求"默认不显示"）。 */
  const [hiddenOpen, setHiddenOpen] = useState(false);
  /**
   * 正在改字的那个自造词（`null` = 没有在改）。
   *
   * 2026-10-30 用户要求给自造词一个改错别字的入口；这里只存"改哪一个、改成什么"，
   * 真正的写入走 `onRenameWord`（页面调 `PATCH .../words/:id` 的 `wordText`）。
   */
  const [renaming, setRenaming] = useState<{ id: string; value: string } | null>(null);
  /** 正在确认删除的自造词 id（
ull = 没有）。 */
  const [deletingId, setDeletingId] = useState<string | null>(null);
  useEffect(() => {
    /** 换了一个字就把收起状态重置（否则上一个字展开了、这个字一打开就是展开的）。 */
    setHiddenOpen(false);
  }, [hanzi?.id]);

  const summary = data?.summary ?? null;
  const groups = data?.wordsByReading ?? null;

  /**
   * ⚠️ 来源顺序见 `./hanziListings.ts` 的文件头：**先信 `/readings`**（它是专门
   *    为这个字查的，且「按书录入」tab 的字卡里根本没有 `listings`）。
   */
  const listingsView = describeHanziListings({
    fromReadings: data?.hanzi.listings ?? null,
    fromCard: shown?.listings ?? null,
    textbooks,
    loading,
  });

  /**
   * 读音 → 那一组（含它造过的词）。
   *
   * ⚠️ `wordsByReading` 的顺序来自后端（主读音在前）；不在 `summary.readings` 里的
   *    历史脏数据**追加在最后**，不静默丢掉。
   */
  const groupOf = new Map((groups ?? []).map((group) => [group.reading, group]));
  const baseReadings = summary?.readings ?? [];
  const extraReadings = (groups ?? [])
    .map((group) => group.reading)
    .filter((reading) => reading !== '' && !baseReadings.includes(reading));
  /** 全部读音（拼音表里的 + 历史脏数据）—— 隐藏的切分按这一份来。 */
  const everyReading = [...baseReadings, ...extraReadings];
  const split = splitReadingsByHidden(everyReading, hiddenReadings);

  /** 这个字现在是不是**已确认**的多音字（只看人工标注，不看拼音库）。 */
  const polyphonic = shown?.polyphonic === true;

  const writeActionsVisible =
    canEdit &&
    (onTogglePolyphonic !== undefined ||
      onStartPinyin !== undefined ||
      onEditHanzi !== undefined ||
      onDeleteHanzi !== undefined);

  return (
    <Modal
      open={hanzi !== null}
      onClose={onClose}
      size="lg"
      title={shown !== null ? `「${shown.charText}」的读音与造词` : '读音与造词'}
      description="所有操作都在这里：每个读音各造词、隐藏不常用的音、改拼音、标注多音字。"
    >
      {shown === null ? null : (
        <div className="space-y-4">
          {/* ---------- 收录情况（它出现在哪几张表上） ---------- */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
            <p className="font-medium">收录在：</p>
            {listingsView.state === 'listed' ? (
              /*
                ⚠️ **逐行全列**（一个 list item 一条收录）：同一个字可以同时在识字表
                   与写字表上，也可能跨册 —— 「多行要全列」是用户明确提过的要求。
              */
              <ul className="mt-1 list-disc space-y-0.5 pl-5">
                {listingsView.entries.map((entry) => (
                  <li key={entry.id}>{entry.line}</li>
                ))}
              </ul>
            ) : (
              <p
                className={
                  listingsView.state === 'unknown'
                    ? 'mt-1 text-amber-700'
                    : listingsView.state === 'none'
                      ? 'mt-1 text-slate-600'
                      : 'mt-1 text-slate-500'
                }
              >
                {listingsView.message}
              </p>
            )}
          </div>

          {/*
            ---------- 这个字还出现在（**同一筛选结果里的其它位置，可点跳转**） ----------
            ⚠️ 用户要求："同一个界面中出现多音字，也能互相跳转" —— 每一项点下去
               滚到那张卡 + 短暂高亮 2 秒；⛔ **不自动打开**它的浮窗（2026-10-20
               用户点名不要），用户自己点那张卡才开（跳转实现与导入预览同一份，
               见 `hanziJump.ts` / `HanziJumpLinks.tsx`）。
            ⚠️ 只出现一次（`occurrences` 为空）⇒ 整块不渲染，不留"这个字还出现在："的空壳。
          */}
          {occurrences.length > 0 && onJumpToOccurrence !== undefined && (
            <HanziJumpLinks occurrences={occurrences} onJump={onJumpToOccurrence} />
          )}

          {/*
            ⚠️ 备注（`hanzi.note`）原来显示在字卡那一行的下面。字卡改成网格之后
               （一格只放拼音 + 字），它没有地方可放了 —— 放到这里，别让它看不见。
               ⛔ 不要因为"卡片上没地方"就把备注从界面上删掉：那是用户自己写的东西。
          */}
          {shown.note !== '' && (
            <p className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
              备注：{shown.note}
            </p>
          )}

          {/* ---------- 这个字的操作（**只有管理员**渲染） ---------- */}
          {writeActionsVisible && (
            <div className="rounded-xl border border-brand-200 bg-brand-50/60 px-3 py-2">
              <p className="text-sm font-semibold text-slate-900">这个字的操作</p>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                {/*
                  ⚠️ **只有一个读音时不显示「标为多音字」**（2026-11-03 用户要求：
                  "如果这个字只有一个音，那么'标为多音字'这个按钮就不需要了"）。
                  判据：`summary.readings.length >= 2`（拼音库里确实给了多个音）。
                  例外：**已经人工标过多音字**的（`polyphonic === true`）仍然显示 ——
                  否则标错了就再也点不回去了（那个按钮这时是"标为单音字"）。
                */}
                {onTogglePolyphonic !== undefined &&
                  (polyphonic || (data?.summary.readings.length ?? 0) >= 2) && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={polyphonicSaving}
                      onClick={onTogglePolyphonic}
                      title={
                        polyphonic
                          ? '取消多音字标注：改回「单音字」'
                          : '按你确认的标为多音字（拼音库里的多读音不算）'
                      }
                    >
                      {polyphonic ? '标为单音字' : '标为多音字'}
                    </Button>
                  )}
                {onStartPinyin !== undefined && (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={pinyinSaving || pinyinOpen}
                    onClick={onStartPinyin}
                    title={pinyinOpen ? '下面就是编辑框' : '改这个字的读音'}
                  >
                    改拼音
                  </Button>
                )}
                {onEditHanzi !== undefined && (
                  <Button size="sm" variant="secondary" onClick={onEditHanzi}>
                    编辑（年级 / 学期 / 备注）
                  </Button>
                )}
                {onDeleteHanzi !== undefined && (
                  <Button size="sm" variant="danger" onClick={onDeleteHanzi}>
                    删除这个字
                  </Button>
                )}
              </div>
              {/*
                ⚠️ 多音字的口径必须写在这里：用户明确要求"**我确认之后**"才算，
                   而不是拼音库有几个读音就算（那会把 280 个一年级生字标出 134 个）。
              */}
              <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">
                {polyphonic
                  ? '已确认标注为多音字 —— 字卡会有琥珀色底色，「只看多音字」也筛得到它。'
                  : '多音字按你这里的标注算：拼音库把变调、古音也算成读音，所以系统不替你判断。'}
              </p>
              {pinyinOpen && onSavePinyin !== undefined && (
                <div className="mt-2">
                  {pinyinAffectsPosition && (
                    <p className="mb-1.5 rounded-lg bg-white/70 px-2 py-1 text-[11px] leading-relaxed text-slate-600">
                      保存后：这个字的<strong>默认读音</strong>会改；你点开的<strong>这张卡</strong>
                      的读音也会一起改（别的、单独标过音的位置不动）。
                    </p>
                  )}
                  <PinyinEditor
                    kind="hanzi"
                    text={shown.charText}
                    pinyin={shown.pinyin}
                    pinyinAuto={shown.pinyinAuto}
                    overridden={shown.pinyinOverridden}
                    alternates={shown.pinyinAlternates}
                    saving={pinyinSaving}
                    error={pinyinError}
                    onSave={onSavePinyin}
                    onCancel={onCancelPinyin ?? onClose}
                  />
                </div>
              )}
            </div>
          )}

          {/* ---------- 读音 ---------- */}
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : error !== null ? (
            <p className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              读音没拿到：{error}
              <button type="button" onClick={onRetry} className="ml-2 font-medium underline">
                重试
              </button>
            </p>
          ) : data === null ? (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              读音汇总没拿到（不是"这个字没有读音"）—— 请刷新重试。
            </p>
          ) : summary === null ? (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              读音汇总没拿到。
            </p>
          ) : summary.readings.length === 0 ? (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              内置字表里没有「{shown.charText}」—— 读音需要手工填。
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-slate-700">读音：</span>
                {summary.readings.map((reading) => (
                  <Badge key={reading} tone={reading === summary.primary ? 'brand' : 'neutral'}>
                    {reading}
                    {reading === summary.primary ? '（最常用）' : ''}
                  </Badge>
                ))}
                {/*
                  ⚠️ 这里说的是**拼音库给了几个读音**（技术层），不是"这个字是不是
                  教学口径的多音字"（那个由管理员标注）。
                  写成「多音字」会跟列表里的徽章撞车 —— 「一」在拼音库里有 3 个读音
                  （变调），但它不是多音字。所以这枚徽章只讲"读音个数"。
                */}
                {summary.polyphonic ? (
                  <Badge tone="success">多个读音（拼音库）</Badge>
                ) : (
                  <Badge tone="neutral">1 个读音（拼音库）</Badge>
                )}
              </div>

              {/* ---------- 按读音分组：每个音各造词 ---------- */}
              <div className="space-y-3">
                {split.visible.map((reading) => {
                  const group = groupOf.get(reading);
                  const words = group?.words ?? [];
                  return (
                    <div
                      key={reading}
                      className="rounded-xl border border-slate-200 px-3 py-2"
                      data-reading={reading}
                    >
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-mono text-base font-semibold text-slate-900">
                          {reading}
                        </span>
                        {group?.primary === true && <Badge tone="brand">最常用</Badge>}
                        <span className="text-xs text-slate-500">
                          造过 {words.length} 个词
                          {words.length === 0 ? ' —— 这个音还没造过词' : ''}
                        </span>
                        {onToggleHiddenReading !== undefined && (
                          <button
                            type="button"
                            onClick={() => onToggleHiddenReading(reading)}
                            className="ml-auto shrink-0 rounded-lg border border-slate-300 bg-white px-2 py-0.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                            title="不常用就收起来：这里不再显示，但随时能点回来"
                          >
                            隐藏这个音
                          </button>
                        )}
                      </div>
                      {words.length > 0 && (
                        <ul className="mt-1 flex flex-wrap gap-1.5">
                          {words.map((word) => (
                            <li
                              key={`${reading}-${word.id}-${word.position}`}
                              className="flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-0.5 text-sm text-slate-800"
                            >
                              <span>{word.wordText}</span>
                              <span className="font-mono text-[11px] text-slate-500">
                                {word.pinyin}
                              </span>
                              {/*
                                **区分课本词 / 自造词**（2026-10-30 用户问："对于自己造的
                                词和录入课本的词语表，你能够区分吧？"）—— 数据里本来就有
                                `word.from_textbook`，这里只是把它显示出来。
                              */}
                              <span
                                className={cn(
                                  'rounded px-1 text-[10px]',
                                  word.fromTextbook
                                    ? 'bg-slate-200 text-slate-600'
                                    : 'bg-amber-100 text-amber-800',
                                )}
                                title={word.fromTextbook ? '来自课本词语表' : '自己造的词（可以改字）'}
                              >
                                {word.fromTextbook ? '课本' : '自造'}
                              </span>
                              {!word.fromTextbook && onRenameWord !== undefined && (
                                <button
                                  type="button"
                                  aria-label={`改「${word.wordText}」的字`}
                                  title="改字（错了可以在这里修）"
                                  onClick={() => setRenaming({ id: word.id, value: word.wordText })}
                                  className="rounded px-1 text-[11px] text-brand-700 hover:bg-brand-50"
                                >
                                  ✏️
                                </button>
                              )}
                              {/*
                                **删除自造词**（2026-11-04 用户要求："造的词要能删除"）。
                                只对自造词出现；课本词是导入的事实，不能从这里删。
                                点一下先变成"确认删除"，再点一次才真的删（防误触）。
                              */}
                              {!word.fromTextbook && onDeleteWord !== undefined && (
                                <button
                                  type="button"
                                  aria-label={`删除「${word.wordText}」`}
                                  title="删除这个词"
                                  onClick={() => setDeletingId(word.id)}
                                  className="rounded px-1 text-[11px] text-rose-600 hover:bg-rose-50"
                                >
                                  🗑
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      {/*
                        **删除确认**：内联在同一个读音组里（不弹第二个窗口）。
                        确认后调 `onDeleteWord`，同时把这个字的读音关联一起清掉。
                      */}
                      {deletingId !== null &&
                        onDeleteWord !== undefined &&
                        words
                          .filter((word) => word.id === deletingId)
                          .map((word) => (
                            <div
                              key={`del-${word.id}`}
                              className="mt-1.5 flex flex-wrap items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-2 py-1"
                            >
                              <span className="text-xs text-rose-800">
                                删除「{word.wordText}」？
                              </span>
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => {
                                  void Promise.resolve(onDeleteWord(word.id, word.wordText))
                                    .finally(() => setDeletingId(null));
                                }}
                              >
                                确认删除
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setDeletingId(null)}>
                                取消
                              </Button>
                            </div>
                          ))}
                      {/*
                        **改字**的内联编辑框：只对自造词出现（点上面的 ✏️ 打开）。
                        放在这个词所在的读音组里 —— 改完读什么音再对一次，不容易改错。
                      */}
                      {renaming !== null &&
                        words.some((word) => word.id === renaming.id) &&
                        onRenameWord !== undefined && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            <input
                              value={renaming.value}
                              onChange={(event) =>
                                setRenaming({ id: renaming.id, value: event.target.value })
                              }
                              className="w-40 rounded-lg border border-slate-300 px-2 py-0.5 text-sm"
                              aria-label="改成什么词"
                            />
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                void Promise.resolve(
                                  onRenameWord(renaming.id, renaming.value),
                                ).finally(() => setRenaming(null));
                              }}
                            >
                              保存
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setRenaming(null)}>
                              取消
                            </Button>
                            <span className="text-[11px] text-slate-500">
                              改了字之后，拼音会按新字重新算。
                            </span>
                          </div>
                        )}
                      {/*
                        ⚠️ **每个读音下面都能造词**（用户原话："针对每个音去造词"）。
                           点它 → 既有的「新增词语」表单；保存后这个字在这个词里
                           的读音会被钉到**这个音**（见 `DictationWorkspace`）。
                      */}
                      {onCreateWord !== undefined && (
                        <div className="mt-1.5">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => onCreateWord(reading)}
                            title={`造一个词，让「${shown.charText}」在这个词里读 ${reading}`}
                          >
                            给这个音造词
                          </Button>
                        </div>
                      )}
                      {/*
                        **一次造多个词**（2026-10-30 用户要求）。
                        与上面那颗并存：想加一个、还想顺手改课本/课时 → 用上面那个；
                        只想快速补一批词 → 用这个（类别自动判断，不用手选）。
                      */}
                      {/*
                        **选词造词**（2026-11-04 用户要求）：从词库里挑现成的词。
                        与「批量造词」并存 —— 那个是自己写，这个是从词库里选。
                      */}
                      {onPickWords !== undefined && (
                        <div className="mt-1.5">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => onPickWords(reading)}
                            title={`从词库里挑含「${shown.charText}」的词（读 ${reading}）`}
                          >
                            选词造词
                          </Button>
                        </div>
                      )}
                      {onBatchCreateWords !== undefined && (
                        <div className="mt-1.5">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => onBatchCreateWords(reading)}
                            title={`一次造多个词，都让「${shown.charText}」读 ${reading}`}
                          >
                            批量造词
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* ---------- 已隐藏的读音（默认收起） ---------- */}
              {split.hidden.length > 0 && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                  <button
                    type="button"
                    onClick={() => setHiddenOpen((prev) => !prev)}
                    className="flex w-full items-center gap-2 text-left text-sm font-medium text-slate-700"
                    aria-expanded={hiddenOpen}
                  >
                    <IconChevronDown
                      size={16}
                      className={cn('shrink-0', hiddenOpen ? 'rotate-180 transition-transform' : 'transition-transform')}
                    />
                    已隐藏的读音（{split.hidden.length}）{hiddenOpen ? ' —— 点这里收起' : ' —— 点开看'}
                  </button>
                  {hiddenOpen && (
                    <ul className="mt-2 space-y-1.5">
                      {split.hidden.map((reading) => {
                        const group = groupOf.get(reading);
                        const words = group?.words ?? [];
                        return (
                          <li
                            key={reading}
                            className="flex flex-wrap items-center gap-2 rounded-lg bg-white px-2 py-1.5"
                            data-hidden-reading={reading}
                          >
                            <span className="font-mono text-sm font-semibold text-slate-900">
                              {reading}
                            </span>
                            <span className="text-[11px] text-slate-500">
                              造过 {words.length} 个词
                            </span>
                            <span className="min-w-0 flex-1 truncate text-[11px] text-slate-500">
                              {words.length > 0
                                ? words.map((word) => word.wordText).join('、')
                                : '这个音还没造过词'}
                            </span>
                            {onToggleHiddenReading !== undefined && (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => onToggleHiddenReading(reading)}
                              >
                                显示
                              </Button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {/*
                    ⚠️ 用户点名要的一句：隐藏只影响**显示**。
                       已有的词、它们的读音（`word_hanzi.reading`）、听写都不变。
                    ⚠️ 家长端（`onToggleHiddenReading === undefined`）多说半句：
                       "这些音是谁藏的" —— 否则家长只会觉得"界面少了几个音"。
                  */}
                  <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">
                    隐藏只影响这里怎么显示：已有的词、它们各自的读音、听写都不会变。
                    {onToggleHiddenReading === undefined
                      ? '这些音由站点管理员隐藏；想看时点开这一栏就能看到。'
                      : ''}
                  </p>
                </div>
              )}

              <p className="text-xs text-slate-500">
                想专门听写这个字的某个音：去「选词预览」，把「读音」那一栏选上
                {summary.polyphonic ? '（有多个读音时可以只挑一个音）' : ''}。
              </p>
            </>
          )}

          <div className="flex justify-end">
            <Button variant="secondary" onClick={onClose}>
              关闭
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
